import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Radar, RefreshCw, Wifi, WifiOff, Ship, Truck, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

// Icone customizado para embarcacao
const shipIcon = (rotation = 0) => L.divIcon({
    html: `<div style="transform: rotate(${rotation}deg); font-size: 22px; color: #06b6d4; text-shadow: 0 0 4px rgba(0,0,0,0.7);">▲</div>`,
    className: 'ais-vessel-marker',
    iconSize: [22, 22],
    iconAnchor: [11, 11],
});

function FitBounds({ posicoes }) {
    const map = useMap();
    useEffect(() => {
        const validas = (posicoes || []).filter(p => p && p.lat != null && p.lng != null);
        if (validas.length > 0) {
            try {
                const bounds = L.latLngBounds(validas.map(p => [p.lat, p.lng]));
                map.fitBounds(bounds.pad(0.3), { maxZoom: 10 });
            } catch (e) { console.warn('Falha ao ajustar bounds do mapa AIS:', e); }
        }
    }, [posicoes, map]);
    return null;
}

export default function RastreamentoAISPage() {
    const [status, setStatus] = useState({ ativo: false, conectado: false, mensagens: 0, ultima_em: null });
    const [posicoes, setPosicoes] = useState([]);
    const [geofences, setGeofences] = useState([]);
    const [historico, setHistorico] = useState({});  // { mmsi: [pontos] }
    const [selectedMmsi, setSelectedMmsi] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchAll = useCallback(async () => {
        try {
            const [st, pos, gf] = await Promise.all([
                axios.get(`${API}/ais/status`),
                axios.get(`${API}/ais/posicoes/ultimas?minutos=180`),
                axios.get(`${API}/geofences`).catch(() => ({ data: [] })),
            ]);
            setStatus(st.data);
            setPosicoes(pos.data.posicoes || []);
            setGeofences((gf.data || []).filter(g => g.ativa));
        } catch {
            toast.error('Erro ao carregar AIS');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAll();
        const t = setInterval(fetchAll, 30000); // refresh a cada 30s
        return () => clearInterval(t);
    }, [fetchAll]);

    const carregarHistorico = async (mmsi) => {
        if (historico[mmsi]) {
            setSelectedMmsi(selectedMmsi === mmsi ? null : mmsi);
            return;
        }
        try {
            const r = await axios.get(`${API}/ais/posicoes/${mmsi}?horas=24`);
            setHistorico(prev => ({ ...prev, [mmsi]: r.data.pontos || [] }));
            setSelectedMmsi(mmsi);
        } catch {
            toast.error('Erro ao buscar historico');
        }
    };

    const centro = posicoes.length > 0 && posicoes[0].lat != null && posicoes[0].lng != null
        ? [posicoes[0].lat, posicoes[0].lng]
        : [-3.5, -52.0]; // default Pará/Xingu

    const ultUpdate = status.ultima_em
        ? new Date(status.ultima_em).toLocaleString('pt-BR')
        : 'nunca';

    return (
        <div className="p-6 space-y-6" data-testid="rastreamento-ais-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-heading text-white flex items-center gap-2">
                        <Radar className="w-7 h-7 text-cyan-400" />
                        Rastreamento AIS
                    </h1>
                    <p className="text-sm text-slate-400 font-mono mt-1">
                        Posicoes em tempo real via aisstream.io
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs font-mono uppercase border ${
                        status.conectado ? 'bg-emerald-900/40 text-emerald-300 border-emerald-700' : 'bg-red-900/40 text-red-300 border-red-700'
                    }`} data-testid="status-ais">
                        {status.conectado ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
                        {status.conectado ? 'CONECTADO' : 'DESCONECTADO'}
                    </span>
                    <span className="text-xs text-slate-500 font-mono">
                        {status.mensagens} msgs | últ: {ultUpdate}
                    </span>
                    <Button size="sm" onClick={fetchAll} variant="outline" data-testid="btn-refresh-ais">
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> Atualizar
                    </Button>
                </div>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="bg-slate-900 border-slate-800">
                    <CardContent className="p-4">
                        <p className="text-xs text-slate-400 font-mono uppercase">Embarcacoes Ativas</p>
                        <p className="text-3xl text-white font-bold mt-1">{posicoes.length}</p>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900 border-slate-800">
                    <CardContent className="p-4">
                        <p className="text-xs text-slate-400 font-mono uppercase">Geofences Ativas</p>
                        <p className="text-3xl text-cyan-300 font-bold mt-1">{geofences.length}</p>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900 border-slate-800">
                    <CardContent className="p-4">
                        <p className="text-xs text-slate-400 font-mono uppercase">Msgs Recebidas</p>
                        <p className="text-3xl text-emerald-300 font-bold mt-1">{status.mensagens}</p>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900 border-slate-800">
                    <CardContent className="p-4">
                        <p className="text-xs text-slate-400 font-mono uppercase">Frota com MMSI</p>
                        <p className="text-3xl text-amber-300 font-bold mt-1">{posicoes.filter(p => p.embarcacao_nome).length}</p>
                    </CardContent>
                </Card>
            </div>

            {/* Mapa */}
            <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                    <CardTitle className="text-white font-heading text-base flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-cyan-400" />
                        Mapa em Tempo Real
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="h-[480px] w-full rounded-sm overflow-hidden border border-slate-800" data-testid="mapa-leaflet">
                        <MapContainer center={centro} zoom={5} style={{ height: '100%', width: '100%' }}>
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; OpenStreetMap'
                            />
                            <FitBounds posicoes={posicoes} />
                            {geofences.map(gf => (
                                <Polygon
                                    key={gf.id}
                                    positions={gf.poligono}
                                    pathOptions={{ color: gf.cor || '#06b6d4', fillOpacity: 0.15, weight: 2 }}
                                >
                                    <Popup>
                                        <strong>{gf.nome}</strong><br/>
                                        {gf.descricao}
                                    </Popup>
                                </Polygon>
                            ))}
                            {posicoes.filter(p => p && p.lat != null && p.lng != null).map(p => (
                                <Marker
                                    key={p.mmsi}
                                    position={[p.lat, p.lng]}
                                    icon={shipIcon(p.cog || 0)}
                                    eventHandlers={{ click: () => carregarHistorico(p.mmsi) }}
                                >
                                    <Popup>
                                        <div style={{ fontSize: 12 }}>
                                            <strong>{p.embarcacao_nome || p.ship_name || 'Sem nome'}</strong><br/>
                                            MMSI: {p.mmsi}<br/>
                                            Vel: {p.sog?.toFixed(1)} nós | Rumo: {p.cog?.toFixed(0)}°<br/>
                                            <span style={{ color: '#666' }}>{new Date(p.timestamp).toLocaleString('pt-BR')}</span>
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}
                            {selectedMmsi && historico[selectedMmsi] && (
                                <Polyline
                                    positions={historico[selectedMmsi].map(p => [p.lat, p.lng])}
                                    pathOptions={{ color: '#fbbf24', weight: 3, opacity: 0.8 }}
                                />
                            )}
                        </MapContainer>
                    </div>
                </CardContent>
            </Card>

            {/* Tabela */}
            <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                    <CardTitle className="text-white font-heading text-base">
                        Embarcacoes ({posicoes.length})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-slate-400 text-center py-6">Carregando...</p>
                    ) : posicoes.length === 0 ? (
                        <p className="text-slate-500 text-center py-6 italic">
                            Nenhuma posicao recebida ainda. Cadastre MMSI em Embarcacoes para receber dados.
                        </p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="border-slate-800 hover:bg-transparent">
                                    <TableHead className="text-slate-400">Embarcacao</TableHead>
                                    <TableHead className="text-slate-400">MMSI</TableHead>
                                    <TableHead className="text-slate-400">Lat/Lng</TableHead>
                                    <TableHead className="text-slate-400 text-right">Vel (nos)</TableHead>
                                    <TableHead className="text-slate-400 text-right">Rumo</TableHead>
                                    <TableHead className="text-slate-400">Atualizado</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {posicoes.map((p) => {
                                    const TipoIcon = p.tipo === 'empurrador' ? Truck : Ship;
                                    return (
                                        <TableRow
                                            key={p.mmsi}
                                            className={`border-slate-800 hover:bg-slate-800/40 cursor-pointer ${selectedMmsi === p.mmsi ? 'bg-cyan-950/30' : ''}`}
                                            onClick={() => carregarHistorico(p.mmsi)}
                                            data-testid={`row-pos-${p.mmsi}`}
                                        >
                                            <TableCell className="text-white">
                                                <span className="inline-flex items-center gap-2">
                                                    {p.tipo && <TipoIcon className="w-3.5 h-3.5 text-cyan-400" />}
                                                    {p.embarcacao_nome || p.ship_name || '-'}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-slate-400 font-mono">{p.mmsi}</TableCell>
                                            <TableCell className="text-slate-400 font-mono text-xs">
                                                {p.lat?.toFixed(4)}, {p.lng?.toFixed(4)}
                                            </TableCell>
                                            <TableCell className="text-slate-300 text-right font-mono">{p.sog?.toFixed(1)}</TableCell>
                                            <TableCell className="text-slate-300 text-right font-mono">{p.cog?.toFixed(0)}°</TableCell>
                                            <TableCell className="text-slate-500 text-xs">
                                                {new Date(p.timestamp).toLocaleString('pt-BR')}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
